/**
 * <TutorPrescriptionPage> — « Travail entre les lives » : ce que le masteur donne à chaque élève à
 * la sortie du live, sur la proposition de Sam (page).
 *
 * Le legacy : `tutor/study-missions/StudyMissionPrescriptionScreen.js` (route
 * `study-mission-prescription`, ouverte par la sortie du live, `LiveModeFooter`), à parité.
 * L'en-tête de Sam, « Pour qui ? », une carte par élève (`<TutorPrescriptionStudentCard>`), puis,
 * au pied, qui reste à servir — nommément (« Reste : Léa B., Karim D. ») : « 1/2 faits » comptait
 * des élèves comme des cases — et la sortie vers le bilan : « Passer cette étape » tant qu'il reste
 * quelqu'un, « Continuer vers le bilan » quand tout le monde a son travail. Les deux mènent au même
 * endroit ; c'est leur poids qui change. Pendant que la présence se lit, le pied se tait.
 *
 * **Les boîtes** sont celles du legacy, mot pour mot (`dialog`) : la confirmation d'un retrait, celle
 * d'un remplacement de la proposition, et les refus du serveur. Une seule à la fois ; les refus
 * n'ont qu'un « OK ». Les aperçus d'une activité et le formulaire de « Recommencer » ne sont pas
 * dessinés ici : l'écran ouvre `<ActivityDetailsModal>` et `<GenerateActivityFlow>` (déjà au DS).
 *
 * Contrôlée : les élèves arrivent avec leur présence, leur proposition, le geste en vol et leur
 * conclusion. Ce qui en découle se dérive ici — qui reste, et les deux verrous (une seule
 * régénération, un seul retrait à la fois, comme le legacy).
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { TutorPrescriptionStudentCard } from "../../organisms/TutorPrescriptionStudentCard"
import { PageTemplate } from "../../templates/PageTemplate"
import {
    type UITutorPrescriptionActivity,
    type UITutorPrescriptionDialog,
    type UITutorPrescriptionStudent,
} from "../../../ports/primary/ui/types/tutorPrescription"
import { joinFrench } from "../../../ports/primary/ui/formatters/textFormatter"
import { TutorPrescriptionIntro } from "./TutorPrescriptionIntro"
import { TutorPrescriptionSkeleton } from "./TutorPrescriptionSkeleton"

/** La colonne du legacy (560 px) : une carte d'élève se lit d'un regard, même au bureau. */
const CONTENT_WIDTH = 560

const GENERIC_ERROR = "Une erreur est survenue. Réessaie dans un instant."

type DialogCopy = { title: string; message: string; confirmLabel: string; danger: boolean; question: boolean }

/** Les mots de chaque boîte — ceux des `DialogM` du legacy. */
function dialogCopy(dialog: UITutorPrescriptionDialog): DialogCopy {
    const info = (title: string, message: string): DialogCopy => ({
        title,
        message,
        confirmLabel: "OK",
        danger: false,
        question: false,
    })
    switch (dialog.kind) {
        case "remove_activity":
            return {
                title: "Retirer cette activité ?",
                message: `${dialog.studentName} ne verra plus « ${dialog.activityTitle} ». Rien n'est ajouté à cette étape.`,
                confirmLabel: "Retirer",
                danger: true,
                question: true,
            }
        case "replace_proposals":
            return {
                title: "Remplacer les exercices proposés ?",
                message: `Les ${dialog.count} exercices de « À ajouter » seront remplacés par une nouvelle proposition. Ses exercices déjà à faire ne bougent pas.`,
                confirmLabel: "Remplacer",
                danger: false,
                question: true,
            }
        case "remove_not_applied":
            return info(
                "Activité non retirée",
                `« ${dialog.activityTitle} » reste dans ses exercices à faire : ${dialog.studentName} l'a déjà commencée, ou elle n'y est plus.`
            )
        case "remove_failed":
            return info(
                "Impossible de retirer les activités",
                "Le retrait n'a pas pu être effectué. Réessaie dans un instant — ses exercices à faire sont inchangés."
            )
        case "prescribe_failed":
            return dialog.alreadyOpened
                ? info(
                      "Mission déjà commencée",
                      "Cet élève a déjà ouvert une activité d'une mission précédente — impossible de l'écraser."
                  )
                : info("Prescription impossible", GENERIC_ERROR)
        case "unprescribe_failed":
        default:
            return dialog.alreadyOpened
                ? info(
                      "Trop tard pour annuler",
                      "L'élève a déjà ouvert une activité de cette mission — on ne peut plus la retirer. Tu peux toujours en discuter directement avec lui."
                  )
                : info("Annulation impossible", GENERIC_ERROR)
    }
}

export type TutorPrescriptionPageProps = {
    /** Les élèves du live, dans l'ordre du live. */
    students: UITutorPrescriptionStudent[]
    /** Premier chargement, rien à montrer : la page montre son squelette. */
    loading?: boolean
    error?: unknown
    onRetry?: () => void

    onOpenActivity?: (student: UITutorPrescriptionStudent, activity: UITutorPrescriptionActivity) => void
    onAskRemoveActivity?: (student: UITutorPrescriptionStudent, activity: UITutorPrescriptionActivity) => void
    onOpenProposal?: (student: UITutorPrescriptionStudent, index: number) => void
    onDropProposal?: (student: UITutorPrescriptionStudent, index: number) => void
    onRegenerate?: (student: UITutorPrescriptionStudent) => void
    onAddActivity?: (student: UITutorPrescriptionStudent) => void
    /** « Donner à faire », ou « Enregistrer » sans rien à donner — l'écran sait lequel. */
    onConfirm?: (student: UITutorPrescriptionStudent) => void
    onUndo?: (student: UITutorPrescriptionStudent) => void
    onRetryDraft?: (student: UITutorPrescriptionStudent) => void
    /** « Passer cette étape » comme « Continuer vers le bilan » : vers le bilan de la séance. */
    onContinue?: () => void

    /** La boîte ouverte. `null` : aucune. */
    dialog?: UITutorPrescriptionDialog | null
    /** Le « oui » d'une boîte qui demande (retirer, remplacer). */
    onDialogConfirm?: () => void
    /** « Annuler », « OK », le voile. */
    onDialogClose?: () => void
    testID?: string
}

export function TutorPrescriptionPage(props: TutorPrescriptionPageProps) {
    const {
        students,
        loading = false,
        error,
        onRetry,
        onOpenActivity,
        onAskRemoveActivity,
        onOpenProposal,
        onDropProposal,
        onRegenerate,
        onAddActivity,
        onConfirm,
        onUndo,
        onRetryDraft,
        onContinue,
        dialog = null,
        onDialogConfirm,
        onDialogClose,
        testID,
    } = props

    const presenceKnown = !students.some((s) => s.presence === "pending")
    const present = students.filter((s) => s.presence === "present")
    const remaining = present.filter((s) => s.outcome == null)
    const regenerating = students.some((s) => s.busy === "regenerating")
    const removing = students.some((s) => s.removingActivityId != null)

    // La boîte garde ses mots pendant sa sortie : `visible` tombe, la dernière boîte reste peinte.
    const lastDialog = React.useRef<UITutorPrescriptionDialog | null>(dialog)
    if (dialog != null) lastDialog.current = dialog
    const copy = lastDialog.current != null ? dialogCopy(lastDialog.current) : null

    const footer = !presenceKnown ? undefined : (
        <Stack gap="2" align="center">
            {present.length > 0 && (
                <Text size="text-sm" color="text-muted" center>
                    {remaining.length > 0
                        ? `Reste : ${joinFrench(remaining.map((s) => s.name))}`
                        : "Tout le monde a son travail"}
                </Text>
            )}
            {remaining.length > 0 ? (
                <Button label="Passer cette étape" variant="ghost" onPress={onContinue} />
            ) : (
                <Button
                    label="Continuer vers le bilan"
                    iconName="arrow-right"
                    iconPosition="right"
                    onPress={onContinue}
                    fullWidth
                />
            )}
        </Stack>
    )

    return (
        <>
            <PageTemplate
                testID={testID}
                loading={loading}
                skeleton={<TutorPrescriptionSkeleton />}
                error={error}
                onRetry={onRetry}
                padding="4"
                maxContentWidth={CONTENT_WIDTH}
                footer={loading ? undefined : footer}
            >
                <Stack gap="3">
                    <TutorPrescriptionIntro withAudience={students.length > 0} />
                    {students.length === 0 ? (
                        <Text color="text-muted" center>
                            Aucun élève présent à ce live.
                        </Text>
                    ) : (
                        students.map((student) => (
                            <TutorPrescriptionStudentCard
                                key={student.id}
                                student={student}
                                regenerateLocked={regenerating && student.busy !== "regenerating"}
                                removalLocked={removing}
                                onOpenActivity={
                                    onOpenActivity != null ? (activity) => onOpenActivity(student, activity) : undefined
                                }
                                onAskRemoveActivity={(activity) => onAskRemoveActivity?.(student, activity)}
                                onOpenProposal={
                                    onOpenProposal != null ? (index) => onOpenProposal(student, index) : undefined
                                }
                                onDropProposal={(index) => onDropProposal?.(student, index)}
                                onRegenerate={() => onRegenerate?.(student)}
                                onAddActivity={() => onAddActivity?.(student)}
                                onConfirm={() => onConfirm?.(student)}
                                onUndo={() => onUndo?.(student)}
                                onRetryDraft={() => onRetryDraft?.(student)}
                            />
                        ))
                    )}
                </Stack>
            </PageTemplate>

            {copy != null && (
                <ConfirmDialog
                    visible={dialog != null}
                    title={copy.title}
                    message={copy.message}
                    confirmLabel={copy.confirmLabel}
                    danger={copy.danger}
                    hideCancel={!copy.question}
                    onConfirm={() => (copy.question ? onDialogConfirm?.() : onDialogClose?.())}
                    onCancel={() => onDialogClose?.()}
                />
            )}
        </>
    )
}
